import { useState } from "react";
import API from "../../utils/api";
import { BASE_URL } from "../../utils/api";
import { useNavigate, Link } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import Loader from "../Shared/Loader";
export default function RegisterForm() {
  const [form, setForm] = useState({
    email: "",
    password: "",
    role: "admin",
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const handleRoleChange = (role) => {
    setForm({ ...form, role });
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const payload = {
        email: form.email,
        password: form.password,
        role: form.role,
      };

      await API.post("/auth/register", payload);

      setSuccess("Registered successfully! Redirecting to login...");
      setTimeout(() => navigate("/login"), 1500);
    } catch (err) {
      console.error("Register Error:", err);
      if (
        err.response?.data?.message?.includes("E11000") &&
        err.response?.data?.message?.includes("email")
      ) {
        setError(
          "This email is already registered. Please try logging in or use a different email."
        );
      } else {
        setError(
          err.response?.data?.message ||
          "Registration failed. Please try again."
        );
      }
    } finally {
      setLoading(false);
    }
  };
  const roleOptions = [
    { label: "Super Admin", value: "super_admin" },
    { label: "Company Admin", value: "admin" },
    { label: "Viewer", value: "viewer", disabled: true }, // visually marked for later
  ];



  return (
    <div className="relative flex flex-col w-full md:flex-row md:w-2/3  bg-white">
      {/* Left Side - Form */}
      <div className="w-full md:w-1/2 flex items-center justify-center bg-white p-6 sm:p-10">
        <form onSubmit={handleRegister} className="w-full max-w-md space-y-5">
          <h2 className="text-3xl font-bold text-gray-900 text-center">
            Create Your Account
          </h2>

          {error && <p className="text-red-500 text-sm text-center">{error}</p>}
          {success && (
            <p className="text-green-600 text-sm text-center">{success}</p>
          )}

          <input
            name="email"
            type="email"
            value={form.email}
            onChange={handleChange}
            placeholder="Email"
            className="w-full p-3 border border-gray-300 rounded outline-none focus:ring-0"
            required
          />

          <div className="relative w-full">
            <input
              name="password"
              type={showPassword ? "text" : "password"}
              value={form.password}
              onChange={handleChange}
              placeholder="Password"
              className="w-full p-3 pr-12 border border-gray-300 rounded outline-none focus:ring-0"
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword((prev) => !prev)}
              className="absolute inset-y-0 right-3 flex items-center text-gray-500 hover:text-gray-700 focus:outline-none"
              tabIndex={-1}
            >
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>

          <div>
            <label className="block mb-2 text-lg font-bold text-gray-700">
              Select Role
            </label>
            <div className="flex flex-wrap justify-center gap-4 sm:gap-6">
              {roleOptions.map(({ label, value, disabled }) => (
                <button
                  type="button"
                  key={value}
                  onClick={() => !disabled && handleRoleChange(value)}
                  className={`flex items-center gap-2 focus:outline-none ${disabled ? "opacity-50 cursor-not-allowed" : ""
                    }`}
                >
                  <div
                    className={`w-4 h-4 rounded-full border-2 ${form.role === value
                        ? "bg-gray-900 border-blue-600"
                        : "border-gray-400"
                      }`}
                  />
                  <span
                    className={`text-sm ${form.role === value
                        ? "text-gray-900 font-medium"
                        : "text-gray-700"
                      }`}
                  >
                    {label}
                  </span>
                </button>
              ))}


            </div>
          </div>

          <button
            type="submit"
            className="w-full py-3 bg-gray-900 text-white font-bold rounded hover:bg-gray-700 transition"
            disabled={loading}
          >
            {loading ? "Registering..." : "Register"}
          </button>

          <p className="text-center text-sm text-gray-600">
            Already have an account?{" "}
            <Link
              to="/login"
              className="text-gray-900 text-md font-bold hover:underline"
            >
              Login here
            </Link>
          </p>
        </form>
      </div>

      {/* Right Side - Welcome Content */}
      <div className="hidden md:flex w-full md:w-1/2 bg-gray-900 text-white items-center justify-center p-10">
        <div className="max-w-sm">
          <h2 className="text-3xl font-bold mb-4">Welcome To BizDirectory!</h2>
          <p className="text-gray-300 mb-6">
            Join now to list your company, explore top businesses, and manage
            your profile in our secure platform.
          </p>

          <div className="bg-gray-700 p-4 rounded shadow">
            <p className="text-sm text-gray-400 mb-2">Recently Added Companies</p>
            <ul className="space-y-2 text-sm">
              <li className="flex justify-between">
                <span>Acme Corp</span>{" "}
                <span className="text-green-400">Verified</span>
              </li>
              <li className="flex justify-between">
                <span>BetaSoft Ltd</span>{" "}
                <span className="text-yellow-400">Pending</span>
              </li>
              <li className="flex justify-between">
                <span>NovaTech</span>{" "}
                <span className="text-green-400">Verified</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Loader */}
      {loading && (
        <div className="absolute inset-0 bg-white bg-opacity-60 flex items-center justify-center z-50">
          <Loader message="Registering your account..." />
        </div>
      )}
    </div>

  );
}
